/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes } from '@adobe-commerce/elsie/lib';
import { Add, ChevronRight } from '@adobe-commerce/elsie/icons';
import { Icon } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/account/components/AddressActions/AddressActions.css';
import { AddressActionsProps } from '@/account/types';

export const AddressActions: FunctionComponent<AddressActionsProps> = ({
  selectable,
  className,
  addNewAddress,
  minifiedView,
  routeAddressesPage,
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    viewAllAddressesButton: `Account.${minifiedViewKey}.Addresses.viewAllAddressesButton`,
    addNewAddressButton: `Account.${minifiedViewKey}.Addresses.addNewAddressButton`,
    differentAddressButton: `Account.${minifiedViewKey}.Addresses.differentAddressButton`,
  });

  const TagType = selectable ? 'span' : 'button';
  const buttonProps = selectable ? {} : { AriaRole: 'button', type: 'button' };

  const nonSelectableViewButton =
    minifiedView && !addNewAddress
      ? translations.viewAllAddressesButton
      : translations.addNewAddressButton;

  const buttonText = selectable
    ? translations.differentAddressButton
    : nonSelectableViewButton;

  return (
    <TagType
      {...buttonProps}
      className={classes([
        'account-actions-address',
        ['account-actions-address--viewall', minifiedView],
        ['account-actions-address--address', !minifiedView],
        ['account-actions-address--selectable', selectable],
        className,
      ])}
      data-testid="showRouteFullAddress"
      onClick={routeAddressesPage}
    >
      <span
        className="account-actions-address__title"
        data-testid="addressActionsText"
      >
        {buttonText}
      </span>
      <Icon
        source={minifiedView && !addNewAddress ? ChevronRight : Add}
        size="32"
      />
    </TagType>
  );
};
